import React, { useState, useEffect } from 'react';
import BasePage from './BasePage'
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid2,
  Container, 
  useTheme, 
  alpha,
  Fade,
  Skeleton
} from '@mui/material';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// Import the Footer component at the top of the file
import Footer from '../components/Footer';
import { useInView } from '../hooks/useInView';

function App() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // Add state for banner image
  const [bannerImage, setBannerImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load the appropriate banner image based on screen size
  useEffect(() => {
    const loadBannerImage = async () => {
      setIsLoading(true);
      try {
        // Get screen width
        const width = window.innerWidth;
        let imageModule;

        // Load only the appropriate image based on screen size
        if (width < 600) { // xs breakpoint
          imageModule = await import('../assets/landing_page_banner_small.webp');
        } else if (width < 900) { // sm breakpoint
          imageModule = await import('../assets/landing_page_banner_medium.webp');
        } else { // md and up
          imageModule = await import('../assets/landing_page_banner_large.webp');
        }

        setBannerImage(imageModule.default);
      } catch (error) {
        console.error('Failed to load banner image:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadBannerImage();

    // Optional: Add resize listener to update image if window size changes significantly
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      const currentBreakpoint = currentWidth < 600 ? 'xs' : currentWidth < 900 ? 'sm' : 'md';
      
      // Only reload if crossing a breakpoint
      const prevBreakpoint = bannerImage?.includes('small') ? 'xs' : 
                              bannerImage?.includes('medium') ? 'sm' : 'md';
      
      if (currentBreakpoint !== prevBreakpoint) {
        loadBannerImage();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Rest of your existing refs and hooks
  const heroRef = React.useRef(null);
  const mobileSubtitleRef = React.useRef(null);
  const featuresRef = React.useRef(null);
  const ctaRef = React.useRef(null);

  const heroVisible = useInView(heroRef, { threshold: 0.2 });
  const mobileSubtitleVisible = useInView(mobileSubtitleRef, { threshold: 0.2 });
  const featuresVisible = useInView(featuresRef, { threshold: 0.2 });
  const ctaVisible = useInView(ctaRef, { threshold: 0.2 });

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/homes');
    } else {
      navigate('/login');
    }
  };

  // First, define the features array before the JSX return
  const features = [
    {
      icon: <HomeWorkIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Save Properties for Analysis',
      description: "Organize properties you're considering and track your evaluation progress."
    },
    {
      icon: <PhotoLibraryIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Spot Hidden Issues & Opportunities',
      description: 'Upload property photos to uncover potential problems and value-adding features before you buy.'
    },
    {
      icon: <AutoAwesomeIcon sx={{ fontSize: 50, color: 'primary.main' }} />, 
      title: 'Automated Room-by-Room Analysis',
      description: 'Get systematic insights for every space - from kitchens to basements - without missing important details.'
    },
    {
      icon: <SmartToyIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Get Expert Insights Instantly',
      description: 'Ask specific questions about any property feature and receive professional-level guidance immediately.'
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Avoid Costly Mistakes',
      description: 'Identify potential repairs, maintenance needs, and deal-breakers before making an offer.'
    },
    {
      icon: <TaskAltIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
      title: 'Track Important Follow-ups',
      description: "Never miss critical inspection points or forget important questions during your property evaluation process."
    }
  ];

  // Create refs for each feature
  const featureRefs = features.map(() => React.useRef(null));
  // Create visibility state for each feature
  const featureVisibility = featureRefs.map(ref => useInView(ref, { threshold: 0.1 }));

  // Add these new states for the word animation
  const wordOptions = ["Buy", "Rent", "Crib"];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  // Define timeoutRef at the component level, not inside useEffect
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  
  // Word rotation effect with special pause for "Crib"
  useEffect(() => {
    let animationTimer: NodeJS.Timeout | null = null;

    const rotateWords = (prevIndex: number) => {
      setIsAnimating(true);
      // clear any pending outer timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // wait for exit animation before switching word
      animationTimer = setTimeout(() => {
        const nextIndex = (prevIndex + 1) % wordOptions.length;
        setCurrentWordIndex(nextIndex);
        setIsAnimating(false);

        // if we just showed "Crib" (index 2), pause 10s, else 2s
        const delay = nextIndex === 2 ? 10000 : 2000;
        timeoutRef.current = setTimeout(() => rotateWords(nextIndex), delay);
      }, 500);
    };

    // kick off after initial 2s
    timeoutRef.current = setTimeout(() => rotateWords(currentWordIndex), 2000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (animationTimer) {
        clearTimeout(animationTimer);
      }
    };
  }, []); // ← run only once

  return (
    <BasePage 
      title="Home | Crib Critic AI"
      description="CribCritic helps you make smarter home buying decisions with AI-powered property insights and thorough evaluations."
      keywords="home evaluation, AI property analysis, real estate assessment"
      canonicalUrl="https://cribcritic.ai/"
    >
      {/* Full-width hero banner */}
      <Fade in={heroVisible} timeout={1000}>
        <Box
          ref={heroRef}
          sx={{
            position: 'relative',
            width: '100%',
            height: { xs: '50vh', sm: '60vh', md: '70vh' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 0,
            overflow: 'hidden', // Ensure nothing spills out
          }}
        >
          {/* Show skeleton while loading */}
          {isLoading && (
            <Skeleton 
              variant="rectangular" 
              width="100%" 
              height="100%" 
              animation="wave"
              sx={{ position: 'absolute', top: 0, left: 0 }}
            />
          )}
          
          {/* Banner image */}
          {bannerImage && (
            <Box
              component="img"
              src={bannerImage}
              alt="Hero banner"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            />
          )}
          
          {/* Darkening overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: 1
            }}
          />

          {/* Content */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              textAlign: 'center',
              px: 3,
              maxWidth: '900px'
            }}
          >
            <Typography
              variant="h2"
              component="h1"
              sx={{
                color: 'white',
                fontWeight: 900,
                mb: 3,
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                transition: 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out',
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
                fontSize: { xs: '2rem', sm: '3rem', md: '3.75rem' } // Responsive font size
              }}
            >
              <Box 
                component="span" 
                sx={{ 
                  display: 'inline-block',
                  position: 'relative',
                  width: '3em', // Give it fixed width
                  height: '1.2em',
                  overflow: 'hidden',
                  verticalAlign: 'bottom',
                  textAlign: 'right', // Align text to the right
                }}
              >
                <Box 
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    color: 'white',
                    animation: isAnimating ? 
                      'slideOutDown 0.5s forwards' : 
                      'slideInDown 0.5s forwards',
                    display: 'block',
                    '@keyframes slideOutDown': {
                      '0%': { transform: 'translateY(0)', opacity: 1 },
                      '100%': { transform: 'translateY(100%)', opacity: 0 }
                    },
                    '@keyframes slideInDown': {
                      '0%': { transform: 'translateY(-100%)', opacity: 0 },
                      '100%': { transform: 'translateY(0)', opacity: 1 }
                    }
                  }}
                >
                  {wordOptions[currentWordIndex] === "Crib" ? (
                    <>
                      <Box component="span" sx={{ color: "#cb6a17" }}>Cr</Box>ib
                    </>
                  ) : wordOptions[currentWordIndex]}
                </Box>
              </Box>{' '}
              <Box component="span" sx={{ color: "#cb6a17" }}>Cr</Box>itically.
            </Typography>
            
            {/* Subtitle visible only on non-mobile screens */}
            <Typography
              variant="h5"
              sx={{ 
                color: 'white',
                mb: 4, 
                fontWeight: 400,
                transition: 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out',
                transitionDelay: '200ms',
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
                textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                display: { xs: 'none', sm: 'block' } // Hide on mobile
              }}
            >
              Upload photos of any property and get comprehensive AI-powered analysis, room-by-room insights, and personalized recommendations to make confident buying decisions.
            </Typography>
            
            <Button
              variant="contained"
              size="large"
              color="primary"
              onClick={handleGetStarted}
              sx={{
                borderRadius: '28px',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: theme => `0 8px 16px -10px ${alpha(theme.palette.grey[900], 0.25)}`,
                transition: 'all 0.3s ease-in-out, transform 0.5s ease-in-out, opacity 0.5s ease-in-out',
                transitionDelay: '300ms',
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: theme => `0 12px 20px -10px ${alpha(theme.palette.grey[900], 0.35)}`
                }
              }}
            >
              {isAuthenticated ? 'Go to My Homes' : 'Get Started'}
            </Button>
          </Box>
        </Box>
      </Fade>

      {/* Enhanced Mobile-only subtitle section */}
      <Fade in={mobileSubtitleVisible} timeout={1000}>
        <Box 
          ref={mobileSubtitleRef}
          sx={{ 
            display: { xs: 'block', sm: 'none' }, 
            py: 4,
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'background.default', // Use theme background instead of gradient
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <Container maxWidth="sm">
            <Paper
              elevation={2} // Match the elevation of feature boxes
              sx={{
                p: 3,
                position: 'relative',
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
                backgroundImage: `radial-gradient(circle at 20% 35%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 50%), radial-gradient(circle at 75% 80%, ${alpha(theme.palette.secondary.main, 0.05)} 0%, transparent 50%)`,
                backdropFilter: 'blur(8px)',
                // background: theme.palette.background.paper, // This respects dark/light mode
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                boxShadow: `0 4px 20px -5px ${alpha(theme.palette.primary.main, 0.15)}`
              }}
            >
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: 12,  // Improved positioning
                  left: 12,  // Improved positioning
                  opacity: 0.15, 
                  transform: 'rotate(-5deg)',
                  color: theme.palette.primary.main,
                }}
              >
              </Box>
              
              <Box sx={{ pl: 1 }}>
                <Typography
                  variant="h5"
                  sx={{ 
                    fontWeight: 500,
                    transition: 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out',
                    transitionDelay: '200ms',
                    opacity: mobileSubtitleVisible ? 1 : 0,
                    transform: mobileSubtitleVisible ? 'translateY(0)' : 'translateY(20px)',
                    position: 'relative'
                  }}
                >
                  Upload photos of any property and get 
                  <Box component="span" sx={{ 
                    fontWeight: 700, 
                    color: theme.palette.primary.main,
                    display: 'inline-block',
                    mx: 0.5
                  }}>
                    comprehensive AI-powered analysis
                  </Box> 
                  , room-by-room insights, and personalized recommendations to make confident buying decisions.
                </Typography>
              </Box>
            </Paper>
            
            {/* Quick start buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 2, 
              gap: 2,
              opacity: mobileSubtitleVisible ? 1 : 0,
              transform: mobileSubtitleVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out',
              transitionDelay: '400ms',
            }}>
              <Button 
                size="small" 
                startIcon={<SmartToyIcon />}
                sx={{ 
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                AI Features
              </Button>
              <Button 
                size="small"
                variant="outlined" 
                startIcon={<PhotoLibraryIcon />}
                sx={{ 
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                How It Works
              </Button>
            </Box>
          </Container>
        </Box>
      </Fade>

      {/* Process Clarification Section */}
      <Box sx={{ 
        backgroundColor: 'background.default',
        py: { xs: 3, sm: 4 },
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Container maxWidth="lg">
          <Typography
            variant="h5"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              mb: 4
            }}
          >
            How It Works
          </Typography>
          
          <Box sx={{ position: 'relative', mb: 4 }}>
            <Grid2 container spacing={3}>
              {[
                { number: 1, title: 'Find Properties', description: 'Browse homes on Zillow, Redfin, or with your agent' },
                { number: 2, title: 'Take Your Own Photos', description: 'Visit the property and capture photos during your walkthrough' },
                { number: 3, title: 'Get AI Analysis', description: 'Receive detailed insights and room-by-room evaluation' },
                { number: 4, title: 'Make Decisions', description: 'Negotiate with confidence using expert insights' }
              ].map((step) => (
                <Grid2 key={step.number} size={{ xs: 12, sm: 6, md: 3 }}>
                  
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                        {step.number}
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                      {step.description}
                    </Typography>
                  </Paper>
                </Grid2>
              ))}
            </Grid2>
          </Box>
          
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mt: 2,
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.info.main, 0.05),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              borderLeft: `4px solid ${theme.palette.info.main}`,
              textAlign: 'left'
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.95rem',
                mb: 1,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <CloseIcon sx={{ fontSize: '1rem', color: 'error.main' }} />
              <Box component="span" sx={{ fontWeight: 700, color: 'info.main' }}>
                Not a property search tool.
              </Box>
              {' '}We don't find homes for you.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.95rem',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <CheckIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
              <Box component="span" sx={{ fontWeight: 700, color: 'info.main' }}>
                We're a property analysis tool.
              </Box>
              {' '}We help you evaluate homes you've already found on Zillow, Redfin, or with your agent.
            </Typography>
          </Paper>
        </Container>
      </Box>

      {/* Target Audience Clarity Section */}
      <Box sx={{ 
        backgroundColor: 'background.secondary',
        py: { xs: 5, sm: 6 },
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: 'text.primary'
              }}
            >
              Perfect for Smart Homebuyers
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1.1rem', sm: '1.2rem' },
                color: 'text.secondary',
                maxWidth: '700px',
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              For homebuyers who want professional-level property analysis without hiring multiple inspectors. 
              Get the insights you need to negotiate with confidence and avoid costly surprises.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Features Section with primary background */}
      <Box sx={{ 
        backgroundColor: 'background.primary',
        py: 8 
      }}>
        <Container maxWidth="lg">
          <Box ref={featuresRef}>
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                textAlign: 'center', 
                mb: 6,
                fontWeight: 600,
                transition: 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out',
                opacity: featuresVisible ? 1 : 0,
                transform: featuresVisible ? 'translateY(0)' : 'translateY(20px)'
              }}
            >
              How CribCritic Works
            </Typography>

            <Grid2 container spacing={4}>
              {features.map((feature, index) => (
                <Grid2 size={{xs: 12, sm: 6, md: 4 }} key={index}>
                  {/* Wrap each individual Paper in its own Fade component */}
                  <Fade in={featureVisibility[index]} timeout={600}>
                    <Paper
                      ref={featureRefs[index]}
                      elevation={2}
                      sx={{
                        p: 4,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        borderRadius: 3,
                        transition: 'transform 0.4s ease-in-out, box-shadow 0.4s ease-in-out',
                        transitionDelay: `${50 * index}ms`,
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: 6
                        }
                      }}
                    >
                      <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                      <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Paper>
                  </Fade>
                </Grid2>
              ))}
            </Grid2>
          </Box>
        </Container>
      </Box>

      {/* Outcome Statements Section */}
      <Box sx={{ 
        backgroundColor: 'background.secondary',
        py: { xs: 6, sm: 8 }
      }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h4" 
            component="h2" 
            sx={{ 
              textAlign: 'center', 
              mb: 6,
              fontWeight: 600
            }}
          >
            The CribCritic Advantage
          </Typography>
          
          <Grid2 container spacing={4}>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  textAlign: 'center',
                  borderRadius: 3,
                  backgroundColor: 'background.paper',
                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  borderLeft: `4px solid ${theme.palette.success.main}`
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'success.main' }}>
                  🔍 Identify Potential Issues Early
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Spot costly repairs, maintenance needs, and structural concerns before making an offer. 
                  Make informed decisions that could save you thousands.
                </Typography>
              </Paper>
            </Grid2>
            
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  textAlign: 'center',
                  borderRadius: 3,
                  backgroundColor: 'background.paper',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  borderLeft: `4px solid ${theme.palette.primary.main}`
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                  💪 Negotiate with Confidence
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Armed with detailed property insights, negotiate better prices and terms. 
                  Use AI-identified issues as leverage points in your discussions.
                </Typography>
              </Paper>
            </Grid2>
            
            <Grid2 size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 4,
                  height: '100%',
                  textAlign: 'center',
                  borderRadius: 3,
                  backgroundColor: 'background.paper',
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  borderLeft: `4px solid ${theme.palette.warning.main}`
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'warning.main' }}>
                  🏠 Make Smarter Investment Decisions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Understand a property's true condition and potential. 
                  Avoid buyer's remorse with comprehensive analysis that reveals hidden opportunities.
                </Typography>
              </Paper>
            </Grid2>
          </Grid2>
        </Container>
      </Box>

      {/* Demo Video Section */}
      <Box sx={{ 
        backgroundColor: 'background.default',
        py: 8 
      }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h4" 
            component="h2" 
            sx={{ 
              textAlign: 'center', 
              mb: 3,
              fontWeight: 600
            }}
          >
            See CribCritic in Action
          </Typography>
          
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              textAlign: 'center', 
              mb: 5,
              maxWidth: '700px',
              mx: 'auto'
            }}
          >
            Watch our demo video to see how CribCritic helps you evaluate properties and make smarter home buying decisions.
          </Typography>
          
          <Box sx={{ 
            position: 'relative',
            width: '100%',
            paddingTop: '56.25%' // 16:9 aspect ratio
          }}>
            <Box
              component="iframe"
              src="https://www.youtube.com/embed/D2Gv_8YkmjI?si=7H9I-r7kujRRcGsA"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: 2,
                boxShadow: 4,
                mb: 2
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Call to Action with secondary background */}
      <Box sx={{ 
        backgroundColor: 'background.secondary',
        py: 8 
      }}>
        <Container maxWidth="lg">
          <Fade in={ctaVisible} timeout={1000}>
            <Paper
              ref={ctaRef}
              elevation={3}
              sx={{
                mt: 2,
                p: 6,
                borderRadius: 4,
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
                backgroundImage: `radial-gradient(circle at 20% 35%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 50%), radial-gradient(circle at 75% 80%, ${alpha(theme.palette.secondary.main, 0.05)} 0%, transparent 50%)`,
                backdropFilter: 'blur(8px)',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 4,
                transition: 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out',
                opacity: ctaVisible ? 1 : 0,
                transform: ctaVisible ? 'translateY(0)' : 'translateY(40px)'
              }}
            >
              <Box sx={{ maxWidth: { md: '60%' } }}>
                <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
                  Ready to analyze your next property like a pro?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                  Join CribCritic today and get expert-level property insights that help you negotiate with confidence and avoid costly surprises.
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                color="secondary"
                onClick={handleGetStarted}
                sx={{
                  borderRadius: '28px',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: { xs: '100%', md: 'auto' },
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: 5
                  }
                }}
              >
                {isAuthenticated ? 'View My Homes' : 'Start Now - It\'s Free'}
              </Button>
            </Paper>
          </Fade>
        </Container>
      </Box>
      
      {/* Footer with Privacy Policy */}
      <Footer />
    </BasePage>
  )
}

export default App
